// FEEL (#423, docs/swing-plan.md §4): where each part sits against the kit,
// and how loose the band is. Pure: the player asks for the seconds to add to
// an onset, the band hook applies the bass accent to the line it builds.
//
// A band's swing is a set of relationships to the ride cymbal, not one ratio
// every part plays alike: the bass locks within a few milliseconds of the ride
// (Hofmann et al. 2017 measured it 2 ms ahead), the comp sits a little behind,
// and the soloist delays the DOWNBEATS by about 30 ms while the offbeats stay
// on the ride's skip note (Nelias et al. 2022: that alone made a line 7.5
// times more likely to be heard as swinging; delaying every note did nothing).
// How the band swings, and where its "and"s fall, is the tune's swing feel
// (music/swingFeel.ts), the kit's measured skip note by default.
//
// Placement is a band field per part (music/bandFeel.ts): constant, the way a
// player's "on top" or "behind" is a habit of a few milliseconds held steady.
// Looseness is the player's: a slow, correlated drift on top of it, never
// white noise (Hennig et al. 2011: listeners prefer long-range-correlated
// fluctuation; a DAW's random humanise is the kind they do not).

import type { BassAccent, Placement } from '@/music/bandFeel';

/** How loose the band is: the reach of its slow drift in time and level.
 *  Tight is exact placement with no drift. */
export type FeelTiming = 'tight' | 'relaxed' | 'loose';
export const FEEL_TIMINGS: FeelTiming[] = ['tight', 'relaxed', 'loose'];

/** Seconds the bass sits off the kit, every note (negative is ahead). */
export const BASS_PLACEMENT_SECONDS: Record<Placement, number> = {
  'on-top': -0.008,
  centre: -0.002,
  behind: 0.006,
};

/** Seconds the comp sits off the kit, every attack. */
export const COMP_PLACEMENT_SECONDS: Record<Placement, number> = {
  'on-top': -0.004,
  centre: 0.004,
  behind: 0.01,
};

/** Seconds the melody's swung downbeats sit late at medium tempos. Its
 *  offbeats stay on the kit's skip note at every placement. */
export const MELODY_DOWNBEAT_SECONDS: Record<Placement, number> = {
  'on-top': 0,
  centre: 0.015,
  behind: 0.03,
};

/** Where each part sits, as the player reads it at a beat. */
export interface FeelPlacement {
  melody: Placement;
  bass: Placement;
  comp: Placement;
}

/**
 * How much of the medium-tempo downbeat delay survives at `bpm`: all of it up
 * to 140, two thirds at 200, 0.4 from 260 (30, 20 and 12 ms when Behind). The
 * soloists Nelias et al. measured delayed less as the tempo rose, and a fixed
 * 30 ms is most of a fast tempo's short note.
 */
export function downbeatDelayScale(bpm: number): number {
  if (!(bpm > 140)) return 1;
  if (bpm <= 200) return 1 - ((bpm - 140) / 60) * (1 / 3);
  if (bpm <= 260) return 2 / 3 - ((bpm - 200) / 60) * (2 / 3 - 0.4);
  return 0.4;
}

/** Seconds a swung downbeat of the melody sits late at `bpm`. */
export function melodyDownbeatDelay(placement: Placement, bpm: number): number {
  return MELODY_DOWNBEAT_SECONDS[placement] * downbeatDelayScale(bpm);
}

/** The reach of the drift: seconds of time and a fraction of level. */
export function loosenessOf(feel: FeelTiming | undefined): { timing: number; velocity: number } {
  if (feel === 'loose') return { timing: 0.008, velocity: 0.08 };
  if (feel === 'relaxed') return { timing: 0.004, velocity: 0.04 };
  return { timing: 0, velocity: 0 };
}

/** A deterministic value in [-1, 1) from integers (an integer hash, no RNG). */
function latticeValue(index: number, octave: number, salt: number): number {
  let h = Math.imul(index | 0, 374761393) ^ Math.imul(octave + 1, 668265263);
  h = Math.imul(h ^ Math.imul(salt + 7, 2246822519), 3266489917);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return ((h >>> 0) / 4294967296) * 2 - 1;
}

/** Octaves of period the drift sums: 1, 2, 4 … 64 beats. */
const DRIFT_OCTAVES = 7;
/** Brings the sum to about a third of its range, so the drift mostly sits
 *  well inside ±1 and reaches the ends only rarely (clamped there). */
const DRIFT_SCALE = 0.4 / Math.sqrt(DRIFT_OCTAVES * 0.22);

/**
 * A slow, correlated drift in [-1, 1] at `beat`: a sum of smooth value-noise
 * bands, one per octave of period from 1 to 64 beats, all the same size, so it
 * approximates 1/f noise. Neighbouring beats move together and the variance
 * grows with the window, where white noise jumps every beat and looks the same
 * in any window. Deterministic: the same beat drifts the same way on every
 * render, and `salt` keeps the parts (and time and level) independent.
 */
export function correlatedDrift(beat: number, salt: number): number {
  let sum = 0;
  for (let octave = 0; octave < DRIFT_OCTAVES; octave++) {
    const x = beat / 2 ** octave;
    const i = Math.floor(x);
    const f = x - i;
    const smooth = f * f * (3 - 2 * f);
    const a = latticeValue(i, octave, salt);
    const b = latticeValue(i + 1, octave, salt);
    sum += a + (b - a) * smooth;
  }
  return Math.max(-1, Math.min(1, sum * DRIFT_SCALE));
}

/** Each part drifts on its own: salts for its time and its level. */
const SALT = { bass: 1, comp: 2, melody: 3 } as const;

/** Seconds of drift on a part's onset at `beat`. */
export function timingDrift(
  feel: FeelTiming | undefined,
  part: keyof typeof SALT,
  beat: number,
): number {
  const { timing } = loosenessOf(feel);
  return timing ? timing * correlatedDrift(beat, SALT[part]) : 0;
}

/** The factor the drift puts on a part's level at `beat` (1 when tight). */
export function velocityDrift(
  feel: FeelTiming | undefined,
  part: keyof typeof SALT,
  beat: number,
): number {
  const { velocity } = loosenessOf(feel);
  return velocity ? 1 + velocity * correlatedDrift(beat, SALT[part] + 10) : 1;
}

/** Seconds to add to a bass or comp onset at `beat`: its placement and the drift. */
export function partShift(
  placement: FeelPlacement | undefined,
  feel: FeelTiming | undefined,
  part: 'bass' | 'comp',
  beat: number,
): number {
  const table = part === 'bass' ? BASS_PLACEMENT_SECONDS : COMP_PLACEMENT_SECONDS;
  return (placement ? table[placement[part]] : 0) + timingDrift(feel, part, beat);
}

interface Voiced {
  beat: number;
  velocity?: number;
}

/**
 * The walking bass leans on the beats its accent names, the way a bassist
 * digs in with the hi-hat: `two-four` eases 1 and 3 to 0.92, `one-three`
 * eases 2 and 4, `even` leaves the line alone, and odd metres keep their line
 * as it is. `barStart` is where bar 1 begins (the pickup); `accentAt` says
 * which accent is in force at a beat (a section or a written feel may differ).
 */
export function accentBassLine<T extends Voiced>(
  notes: T[],
  beatsPerBar: number,
  barStart: number,
  accentAt: (beat: number) => BassAccent,
): T[] {
  if (beatsPerBar % 2 !== 0) return notes;
  return notes.map((note) => {
    const accent = accentAt(note.beat);
    if (accent === 'even') return note;
    const inBar = note.beat - barStart;
    const pos = ((inBar % beatsPerBar) + beatsPerBar) % beatsPerBar;
    if (Math.abs(pos - Math.round(pos)) > 1e-3) return note;
    const onTwoOrFour = Math.round(pos) % 2 === 1;
    const eased = accent === 'two-four' ? !onTwoOrFour : onTwoOrFour;
    if (!eased) return note;
    return { ...note, velocity: (note.velocity ?? 1) * 0.92 };
  });
}
